import { useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { LayoutGrid, KeyRound, Shield, LogOut, Menu, X, Sun, Moon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { cn, initials } from '../lib/utils';
import { NotificationBell } from './NotificationBell';
import { APP_VERSION } from '../version';

const NAVIGATION = [
    { name: 'Catalogue', href: '/', icon: LayoutGrid, end: true },
    { name: 'Mes accès', href: '/mes-acces', icon: KeyRound, end: false },
];

const ADMIN_NAVIGATION = [{ name: 'Administration', href: '/admin', icon: Shield, end: false }];

export function Layout({ children }: { children: ReactNode }) {
    const { user, logout, isAdmin } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const navigation = [...NAVIGATION, ...(isAdmin ? ADMIN_NAVIGATION : [])];

    const linkClass = (isActive: boolean) =>
        cn(
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
            isActive
                ? 'bg-white/15 text-[var(--color-green)]'
                : 'text-white/80 hover:text-[var(--color-green)] hover:bg-white/5'
        );

    /* L'en-tête est un aplat de marque : il reste marine dans les deux thèmes,
       et son contenu est donc calé sur le marine, pas sur les jetons de thème.
       Blanc à 15,9:1 et vert à 11,97:1 dessus, dans un cas comme dans l'autre. */
    return (
        <div className="min-h-screen flex flex-col bg-[var(--color-bg)]">
            <header className="bg-[var(--color-navy)] sticky top-0 z-40 border-b border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-3 shrink-0">
                            <img
                                src="/logo-clippit.png"
                                alt=""
                                className="h-9 w-auto logo-wiggle"
                                aria-hidden
                            />
                            <span className="font-semibold text-lg text-white">
                                <span className="text-[var(--color-green)]">Hub</span>
                            </span>
                        </Link>

                        <nav className="hidden md:flex items-center gap-1" aria-label="Navigation principale">
                            {navigation.map((item) => (
                                <NavLink
                                    key={item.href}
                                    to={item.href}
                                    end={item.end}
                                    className={({ isActive }) => linkClass(isActive)}
                                >
                                    {item.name}
                                </NavLink>
                            ))}
                        </nav>

                        <div className="flex items-center gap-2">
                            <NotificationBell />

                            <button
                                type="button"
                                onClick={toggleTheme}
                                className="hidden sm:flex p-2 rounded-lg hover:bg-white/10 transition-colors"
                                aria-label={
                                    theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'
                                }
                            >
                                {theme === 'dark' ? (
                                    <Sun className="w-4 h-4 text-white/70" />
                                ) : (
                                    <Moon className="w-4 h-4 text-white/70" />
                                )}
                            </button>

                            <div className="hidden sm:flex items-center gap-2 pl-1">
                                <div
                                    className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center shrink-0"
                                    aria-hidden
                                >
                                    <span className="text-white text-xs font-semibold">
                                        {initials(user?.displayName, user?.email)}
                                    </span>
                                </div>
                                <span className="hidden lg:block text-sm font-medium text-white max-w-[140px] truncate">
                                    {user?.displayName ?? '…'}
                                </span>
                                <button
                                    type="button"
                                    onClick={logout}
                                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                                    aria-label="Se déconnecter"
                                >
                                    <LogOut className="w-4 h-4 text-white/70" />
                                </button>
                            </div>

                            <button
                                type="button"
                                className="md:hidden p-2 text-white rounded-lg hover:bg-white/10"
                                onClick={() => setMobileMenuOpen((open) => !open)}
                                aria-expanded={mobileMenuOpen}
                                aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                            >
                                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                </div>

                {mobileMenuOpen && (
                    <nav
                        className="md:hidden border-t border-white/10 px-4 py-3 space-y-1"
                        aria-label="Navigation mobile"
                    >
                        {navigation.map((item) => {
                            const Icon = item.icon;
                            const isActive = item.end
                                ? location.pathname === item.href
                                : location.pathname.startsWith(item.href);

                            return (
                                <Link
                                    key={item.href}
                                    to={item.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={cn(
                                        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium',
                                        isActive ? 'bg-white/15 text-[var(--color-green)]' : 'text-white/80'
                                    )}
                                >
                                    <Icon className="w-4 h-4" aria-hidden />
                                    {item.name}
                                </Link>
                            );
                        })}

                        <hr className="border-white/10 my-2" />

                        <button
                            type="button"
                            onClick={toggleTheme}
                            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-white/80 w-full"
                        >
                            {theme === 'dark' ? (
                                <Sun className="w-4 h-4" aria-hidden />
                            ) : (
                                <Moon className="w-4 h-4" aria-hidden />
                            )}
                            {theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
                        </button>

                        <button
                            type="button"
                            onClick={logout}
                            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-white/80 w-full"
                        >
                            <LogOut className="w-4 h-4" aria-hidden />
                            Se déconnecter
                        </button>
                    </nav>
                )}
            </header>

            <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">{children}</main>

            <footer className="border-t border-[var(--color-border)]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
                    <p className="text-center text-xs text-[var(--color-text-muted)]">
                        Clippit Hub · v{APP_VERSION}
                    </p>
                </div>
            </footer>
        </div>
    );
}
